<template>
  <div
    class="card-carousel"
    :class="{ 'card-carousel--hidden': !isVisible }"
    :style="`background-color:${backgroundColor}`"
  >
    <IconArrowDown
      class="card-carousel__navigation-icon card-carousel__navigation-icon--previous"
      :class="{ 'card-carousel__navigation-icon--disabled': navigationSlideIndex === 0 }"
      @click="navigateToPreviousSlide"
    />
    <IconArrowDown
      class="card-carousel__navigation-icon card-carousel__navigation-icon--next"
      :class="{
        'card-carousel__navigation-icon--disabled': navigationSlideIndex === images.length - 1,
      }"
      @click="navigateToNextSlide"
      @mouseover="loadAllImages"
    />
    <Carousel
      :navigateTo="navigationSlideIndex"
      :paginationEnabled="false"
      :mouseDrag="false"
      :perPage="1"
      :speed="300"
    >
      <Slide v-for="(medium, index) in images" :key="index + medium.mediaUrl">
        <div class="card-carousel__item">
          <video
            class="card-carousel__video"
            v-if="isVideo(medium.mediaUrl)"
            muted
            autoplay
            loop
            playsinline
            :src="medium.mediaUrl"
          />
          <ImageContentful
            class="card-carousel__image"
            :class="{
              'card-carousel__image--contain': isShoes && !isEditorial(medium),
              'card-carousel__image--greyed-out': isSoldOut,
              'card-carousel__image--first': index === 0,
            }"
            :alt="medium.mediaAltText"
            :isBackgroundImage="true"
            :options="{
              imageCompression: 81,
              format: 'jpg',
              retinaSupportFactor: 0.25,
              fixedWidth: CARD_IMAGE_WIDTH,
              setHeight: false,
              backgroundColor: '#f7f7f7',
            }"
            :src="medium.mediaUrl"
          />
        </div>
      </Slide>
    </Carousel>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { Carousel, Slide } from 'vue-carousel/src'
import { BADGE_BACKGROUND_COLORS } from '@/config/constants'
import { PRODUCT_TYPES } from '@/stores/product'
import { determineIfSoldOut } from '@/helpers/product-availabilty.js'
import isVideo from '@/helpers/video'
import IconArrowDown from '@/components/_icons/IconArrowDown.svg'
import ImageContentful, { CONTENTFUL_IMAGE_FIT } from '@/components/_shared/ImageContentful.vue'
import { CARD_IMAGE_WIDTH } from './CardVisualBox.vue'

const PRELOAD_IMAGE_COUNT = 2

export default {
  name: 'CardCarousel',
  components: {
    Carousel,
    IconArrowDown,
    ImageContentful,
    Slide,
  },
  props: {
    backgroundColor: {
      type: String,
      default: '#f7f7f7',
    },
    currentVariant: {
      type: Object,
      required: true,
    },
    galleryMargin: {
      type: Number,
      default: 0,
    },
    isVideoControlVisible: {
      type: Boolean,
      default: true,
    },
    isVisible: {
      type: Boolean,
      default: true,
    },
    product: {
      type: Object,
      required: true,
    },
    slideIndex: {
      type: [Number, Array],
      required: true,
    },
  },

  data() {
    return {
      areAssetsChanging: false,
      BADGE_BACKGROUND_COLORS,
      CONTENTFUL_IMAGE_FIT,
      hasClickBeenReportedToDy: false,
      isLoadingAllImages: false,
      CARD_IMAGE_WIDTH,
      isSlideAnimationEnabled: false,
    }
  },

  computed: {
    ...mapGetters({
      vpHeight: 'viewport/height',
      vpWidth: 'viewport/width',
      getFeature: 'featureToggle/getFeature',
    }),

    isSoldOut() {
      return determineIfSoldOut(
        this.currentVariant.skus || [],
        this.currentVariant.isRaffle,
        this.currentVariant.hasRaffleEnded
      )
    },

    navigationSlideIndex() {
      return this.isSlideAnimationEnabled ? this.slideIndex : [this.slideIndex, false]
    },

    images() {
      // In order to show a different image on hover, we remove the first (default) image
      const assets = this.currentVariant.productVariantData?.assets
      return this.isLoadingAllImages ? assets.slice(1) : assets.slice(1, PRELOAD_IMAGE_COUNT + 1)
    },

    isShoes() {
      return this.currentVariant?.spreeProduct?.productType === PRODUCT_TYPES.shoes
    },
  },

  watch: {
    images: 'resetGalleryIndex',
  },

  mounted() {
    this.$emit('mounted')
    this.isSlideAnimationEnabled = true
  },

  methods: {
    navigateToSlide(slideIndex) {
      // index equals slideIndex as long as it's between 0 and (this.images.length - 1)
      const index = Math.max(Math.min(slideIndex, this.images.length - 1), 0)
      this.$emit('set-slide-index', index)
    },

    navigateToPreviousSlide() {
      this.navigateToSlide(this.slideIndex - 1)
      this.reportClickToDy()
    },

    navigateToNextSlide() {
      this.navigateToSlide(this.slideIndex + 1)
      this.reportClickToDy()
    },

    loadAllImages() {
      this.isLoadingAllImages = true
    },

    reportClickToDy() {
      if (this.hasClickBeenReportedToDy) return
      const cardGalleryTest = this.getFeature('cardGalleryTest')
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.campaignClick,
        campaignIds: cardGalleryTest?.campaignIds,
      })
      this.hasClickBeenReportedToDy = true
    },

    async resetGalleryIndex() {
      this.isSlideAnimationEnabled = false
      await this.$nextTick()
      this.isSlideAnimationEnabled = true
    },

    isEditorial(image = {}) {
      return image?.isEditorialImage
    },

    isVideo,
  },
}
</script>

<style lang="scss" scoped>
$greyed-out-opacity: 0.35;

.card-carousel {
  position: absolute;
  top: 0;
  z-index: 2;
  height: 100%;
  width: 100%;
  transition: opacity 200ms;

  &--hidden {
    opacity: 0;
  }

  &__image,
  &__item {
    height: 100%;
  }

  &__item {
    position: relative;
  }

  &__image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;

    &--contain {
      background-size: calc(100% - 40px) auto;

      @media ($mq-large) {
        background-size: calc(100% - 80px) auto;
      }
    }

    &--greyed-out {
      opacity: $greyed-out-opacity;
    }
  }

  &__video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__navigation-icon {
    position: absolute;
    z-index: 1;
    width: 60px;
    height: 60px;
    padding: 10px;
    top: 50%;
    opacity: 0;
    transition: opacity $transition-time-slow;

    &--next {
      transform: translateY(-50%) rotate(-90deg);
      right: 0;
    }

    &--previous {
      transform: translateY(-50%) rotate(90deg);
      left: 0;
    }

    .is-selected & {
      opacity: 1;

      &--disabled {
        opacity: 0.2;
      }
    }
  }
}

/deep/ .VueCarousel {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

/deep/ .VueCarousel-wrapper {
  height: 100%;
}

/deep/ .VueCarousel-inner {
  height: 100% !important;
  flex-basis: 100% !important;
}
</style>
